<template>
  <div class="text-center">
    <v-dialog v-model="dialog" width="500">
      <template v-slot:activator="{ on, attrs }">
        <v-btn dense color="red lighten-2" dark v-bind="attrs" v-on="on">
          Export
        </v-btn>
      </template>

      <v-card>
        <v-card-title class="text-h5 pa-2 primary white--text">
          Export
        </v-card-title>

        <v-container>
          <div class="flex flex-col space-y-2.5">
            <div class="flex space-x-2.5">
              <v-text-field type="date" @keyup="e => updateOptions('dateStart', e.target.value)" hide-details="auto"
                outlined dense label="Date Start" />
              <v-text-field type="date" @keyup="e => updateOptions('dateEnd', e.target.value)" hide-details="auto"
                outlined dense label="Date End" />
            </div>
            <v-autocomplete :loading="fetchingCategories" @change="onCategoryChange" small-chips deletable-chips
              :items="categoryList" item-value="id" item-text="description" hide-details="auto" outlined dense
              label="Categories" return-object />
          </div>
        </v-container>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <div>
            <div class="flex space-x-2.5">
              <v-btn :loading="fetching" color="error" dense @click="getExpensesPrintout">
                PDF
              </v-btn>
              <v-btn :loading="fetching" @click="getExpensesCSV" color="primary" dense>
                CSV
              </v-btn>
            </div>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import appAxios from '../../../../extra-functions/axios.extra'
import { downloadCSV, formatAccounting, getCurrentDate, useWatchEffect } from '../../../../lib/utils'
import ExpenseReport from '../../../../pdf/expense-reports.pdf'

const [watchEffect, clearWatchEffect] = useWatchEffect()

export default {
  name: 'ExportExpense',
  data() {
    return {
      fetchingCategories: false,
      categoryList: [],
      selectedCategoryName: null,
      options: {},
      dialog: false,
    }
  },
  computed: {
    ...mapState({
      authToken: state => state.auth.token,
      storeId: state => state.settings.storeId,
    }),
    appAxios: function () {
      return appAxios(this.authToken)
    },
  },
  watch: {
    dialog(open) {
      if (open) {
        this.watchEffectExpenseCategories()
      }
    },
  },
  destroyed() {
    clearWatchEffect()
  },
  methods: {
    watchEffectExpenseCategories() {
      watchEffect((onCleanUp) => {
        const controller = new AbortController()
        onCleanUp(() => {
          controller.abort()
        })
        this.getExpenseCategories(controller)
      }, 'getExpenseCategories')
    },
    /**
     * Description
     * @param {AbortController} [controller]
     * @returns {any}
     */
    async getExpenseCategories(controller) {
      try {
        this.fetchingCategories = true
        const { appAxios } = this
        const { data } = await appAxios.get('/expense-categories', {
          signal: controller.signal,
        })
        this.categoryList = data
      } catch (e) {
        console.error(e)
        this.$alertSwalError('oops something went wrong')
      } finally {
        this.fetchingCategories = false
      }
    },
    async getExpensesPrintout() {
      try {
        const { appAxios, storeId } = this
        if (this.fetching) return
        const params = { ...this.options, storeId }
        this.fetching = true
        const { data } = await appAxios.get('/stores/expenses-report', {
          params,
        })
        const expenseReport = new ExpenseReport()
        const items = data.expenses.map(item => {
          return [
            { content: item.category, styles: { halign: 'center' } },
            { content: item.description },
            { content: getCurrentDate(new Date(item.date), 'en-US'), styles: { halign: 'center' } },
            { content: formatAccounting(item.cost), styles: { halign: 'right' } },
          ]
        })
        const storeName = data.store.toLowerCase()
        const expenseCategory = this.selectedCategoryName.toLowerCase().replace(' ', '_')
        expenseReport.generate({
          namePrefix: `${storeName}_${expenseCategory}`,
          avgDailyExpense: formatAccounting(data.avg_daily_expense),
          dateRange: `${getCurrentDate(new Date(data.date_start), 'en-US')} - ${getCurrentDate(new Date(data.date_end), 'en-US')}`,
          totalExpenses: formatAccounting(data.total_expenses),
        }, items)
        this.dialog = false
      } catch (e) {
        if (!e.response) {
          console.error(e.message)
          return
        }
        const { status, data: { errors } } = e.response
        if (status === 422) {
          this.errors = errors
        }
      } finally {
        this.fetching = false
      }
    },
    async getExpensesCSV() {
      try {
        const { appAxios, storeId } = this
        if (this.fetching) return
        const params = { ...this.options, storeId }
        this.fetching = true
        const { data } = await appAxios.get('/stores/expenses-report', {
          params,
        })
        const items = data.expenses.map(item => {
          return [
            item.category,
            item.description,
            getCurrentDate(new Date(item.date)),
            item.cost,
          ]
        })
        const headers = ['Category', 'Desc', 'Date', 'Cost']
        const storeName = data.store.toLowerCase()
        const expenseCategory = this.selectedCategoryName.toLowerCase().replace(' ', '_')
        const time = new Date().getTime()
        const fileName = `${storeName}_${expenseCategory}_expenses_${time}.csv`
        downloadCSV([headers, ...items], fileName)
      } catch (e) {
        if (!e.response) {
          console.error(e.message)
          return
        }
        const { status, data: { errors } } = e.response
        if (status === 422) {
          this.errors = errors
        }
      } finally {
        this.fetching = false
      }
    },
    updateOptions(key, value) {
      this.options = {
        ...this.options,
        [key]: value,
      }
    },
    onCategoryChange(expenseCategory) {
      this.selectedCategoryName = expenseCategory.description
      this.updateOptions('expenseCategory', expenseCategory.id)
    },
  },
}
</script>
